import { CustomFieldTypeEnum, IQuestion } from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import {
  MCheckbox,
  MCustomNumberInput,
  MInput,
} from '../../../../../../components/Monetize';
import { DatePicker } from '../../../../../../components/Monetize/DatePicker/DatePicker';
import { MainSearchInputV2 } from '../../../../../../components/Monetize/MCustomSelect/components/MainSearchInputV2';
import { MCustomMultiSelect } from '../../../../../../components/Monetize/MCustomSelect/MCustomMultiSelect';
import { MCustomSelect } from '../../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { CustomFieldsAllData } from '../../../../../../hooks/useCustomFieldsAll';
import { logger } from '../../../../../../services/logger';

interface QuestionFormCustomFieldProps {
  question: IQuestion;
  filter?: Maybe<Record<string, any>>;
  customFieldsData: CustomFieldsAllData;
  value: any;
  onChange: (value: any) => void;
}

export const QuestionFormCustomField = ({
  question,
  filter,
  customFieldsData,
  value,
  onChange,
}: QuestionFormCustomFieldProps) => {
  const { customFieldEntity, customFieldType } = question;

  if (
    !question.customField ||
    !customFieldEntity ||
    !customFieldType ||
    !customFieldsData.fieldsByKey[
      `${customFieldEntity}.${question.customField}`
    ]
  ) {
    logger.warn(
      'Invalid custom field, custom field data points are missing',
      question,
    );
    return null;
  }

  const customField =
    customFieldsData.fieldsByKey[
      `${customFieldEntity}.${question.customField}`
    ];

  if (customField.type === CustomFieldTypeEnum.CHECKBOX) {
    return (
      <MCheckbox
        value={value ?? false}
        isChecked={!!value}
        onChange={(event) => onChange(event.target.checked)}
      />
    );
  }

  if (customField.type === CustomFieldTypeEnum.DATE) {
    return (
      <DatePicker
        value={value}
        onChange={onChange}
        minDate={filter?.min}
        maxDate={filter?.max}
        boxProps={{ mt: '0 !important', width: '100%' }}
      />
    );
  }

  if (customField.type === CustomFieldTypeEnum.DROPDOWN) {
    return (
      <MCustomSelect
        value={value || ''}
        useMainInputAsSearchInput
        MainInputComponent={MainSearchInputV2}
        itemTitle="label"
        itemValue="value"
        items={customField.values || []}
        placeholder={`Select...`}
        onChange={onChange}
      />
    );
  }

  if (customField.type === CustomFieldTypeEnum.MULTI_SELECT) {
    const multiSelectValue = Array.isArray(value)
      ? value
      : value
        ? [value]
        : [];

    return (
      <MCustomMultiSelect
        clearable
        value={multiSelectValue}
        itemTitle="label"
        itemValue="value"
        items={customField.values || []}
        multiple
        placeholder={`Select...`}
        closeButtonText="Apply"
        onChange={onChange}
        onClear={() => onChange([])}
      />
    );
  }

  if (customField.type === CustomFieldTypeEnum.NUMBER) {
    return (
      <MCustomNumberInput
        width="100%"
        value={value ?? ''}
        onChange={(valueAsString) => onChange(valueAsString)}
        min={filter?.min ?? 1}
        max={filter?.max ?? Number.MAX_SAFE_INTEGER}
      />
    );
  }

  if (customField.type === CustomFieldTypeEnum.SINGLE_LINE_TEXT) {
    return (
      <MInput
        mt="0 !important"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  logger.warn('Unhandled custom field type', customField.type);

  return null;
};
